import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Hotel, FileText, Users, MessageSquareQuote, Flame, Clock, X } from "lucide-react";

const hoursLeft = (expiresAt) => Math.max(0, (new Date(expiresAt) - Date.now()) / 36e5);

export default function Dashboard() {
  const [stats, setStats] = useState({ properties: 0, leads: 0, posts: 0, testimonials: 0 });
  const [recent, setRecent] = useState([]);
  const [holds, setHolds] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, l, b, t] = await Promise.all([
        api.get("/admin/properties"),
        api.get("/admin/leads"),
        api.get("/admin/blog"),
        api.get("/admin/testimonials"),
      ]);
      setStats({
        properties: p.data.length,
        leads: l.data.length,
        posts: b.data.length,
        testimonials: t.data.length,
      });
      setRecent(l.data.slice(0, 8));
    })();
    api.get("/admin/date-holds").then((r) => setHolds(r.data || [])).catch(() => {});
  }, []);

  const cancelHold = async (id) => {
    if (!confirm("Cancel this hold? The guest's dates will be released immediately.")) return;
    try {
      await api.delete(`/admin/date-holds/${id}`);
      setHolds((h) => h.filter((x) => x.id !== id));
      toast.success("Hold cancelled — dates released");
    } catch {
      toast.error("Could not cancel hold");
    }
  };

  const cards = [
    { icon: Hotel, label: "Properties", value: stats.properties },
    { icon: Users, label: "Leads", value: stats.leads },
    { icon: FileText, label: "Stories", value: stats.posts },
    { icon: MessageSquareQuote, label: "Testimonials", value: stats.testimonials },
  ];

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Overview</div>
      <h1 className="font-display text-4xl mt-3">Welcome back.</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white border border-zinc-200 rounded-md p-6">
              <Icon className="w-5 h-5 text-zinc-500" />
              <div className="font-display text-4xl mt-4">{c.value}</div>
              <div className="text-xs text-zinc-500 mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      {holds.length > 0 && (
        <>
          <h2 className="font-display text-2xl mt-12 mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-amber-500" /> Active 48-hour holds
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="active-holds-widget">
            {holds.map((h) => {
              const hrs = hoursLeft(h.expires_at);
              const expiring = hrs <= 6;
              return (
                <div key={h.id} className={`bg-white border rounded-md p-5 ${expiring ? "border-amber-400 bg-amber-50/50" : "border-zinc-200"}`} data-testid={`hold-card-${h.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium capitalize">{(h.property_slug || "").replace(/-/g, " ")}</div>
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${expiring ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-600"}`}>
                        <Clock className="w-3 h-3" /> {hrs < 1 ? "<1h left" : `${Math.floor(hrs)}h left`}
                      </span>
                      <button onClick={() => cancelHold(h.id)} title="Cancel hold" className="text-zinc-400 hover:text-red-600 p-0.5 rounded" data-testid={`cancel-hold-${h.id}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm mt-2">{h.start} → {h.end}</div>
                  <div className="text-xs text-zinc-500 mt-2">{[h.name, h.phone, h.email].filter(Boolean).join(" · ")}</div>
                  {expiring && <div className="text-xs text-amber-700 font-medium mt-2">Expiring soon — nudge this guest to confirm with an advance.</div>}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="flex items-center justify-between mt-12 mb-4">
        <h2 className="font-display text-2xl">Recent leads</h2>
        <Link to="/admin/leads" className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-4">All leads →</Link>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-left">Event</th>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-zinc-500">No leads yet.</td></tr>
            )}
            {recent.map((l) => (
              <tr key={l.id} className="border-t border-zinc-100">
                <td className="p-3 font-medium">{l.name}</td>
                <td className="p-3">{l.phone}</td>
                <td className="p-3">{l.event_type}</td>
                <td className="p-3">{l.property_interested}</td>
                <td className="p-3 text-zinc-500">{new Date(l.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
